import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { contactsApi } from 'src/Services/API';
import { ContactDto, CreateContactDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

type UpdateContactParams = {
  contactId: string;
  updateData: CreateContactDto;
};

export const useUpdateContact = (
  options?: UseMutationOptions<ContactDto, unknown, UpdateContactParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<ContactDto, unknown, UpdateContactParams>(
    ['update-contact', assetId],
    async ({ contactId, updateData }: UpdateContactParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await contactsApi.contactsControllerUpdate(contactId, assetId, updateData);
      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // Invalidate relevant queries to refresh data
        queryClient.invalidateQueries(['fetch-contacts', assetId]);
        queryClient.invalidateQueries(['fetch-contact', assetId, variables.contactId]);
        options?.onSuccess?.(data, variables, context);
        trackEvent('Contact', 'update', data.displayName);
      },
    }
  );
};

export default useUpdateContact;
